import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function parseBRLInput(v: string | number): number {
  const s = String(v).trim().replace(/\s|R\$\s?/g, '')
  if (s === '' || s === '-') return 0
  if (s.includes(',')) {
    if (!s.includes('.') || s.lastIndexOf(',') > s.lastIndexOf('.')) {
      return parseFloat(s.replace(/\./g, '').replace(',', '.')) || 0
    }
    return parseFloat(s.replace(/,/g, '')) || 0
  }
  const parts = s.split('.')
  if (parts.length > 2) return parseFloat(s.replace(/\./g, '')) || 0
  if (parts.length === 2 && parts[1].length === 3 && /^\d+$/.test(parts[1])) {
    return parseFloat(s.replace('.', '')) || 0
  }
  return parseFloat(s) || 0
}

export function formatCPF(cpf: string): string {
  return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4")
}

export function formatPhone(phone: string): string {
  return phone.replace(/(\d{2})(\d{4,5})(\d{4})/, "($1) $2-$3")
}

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value)
}

export function formatDate(date?: Date | string | null): string {
  if (!date) return '—'
  const iso = typeof date === 'string' ? date : date.toISOString()
  const [year, month, day] = iso.slice(0, 10).split('-')
  if (!year || !month || !day) return '—'
  return `${day}/${month}/${year}`
}

/**
 * Converte "YYYY-MM-DD" → "DD/MM/AAAA" para exibir nos inputs do formulário.
 * Aceita strings com timestamp (ex: "2022-02-17T00:00:00") e extrai só a data.
 */
export function isoDateToBR(v: string = ''): string {
  if (!v) return ''
  const match = v.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return v
  return `${match[3]}/${match[2]}/${match[1]}`
}

/**
 * Converte "DD/MM/AAAA" → "YYYY-MM-DD" para enviar à API.
 * Aceita também formato ISO (retorna sem alteração).
 */
export function brDateToISO(v: string = ''): string {
  if (!v) return ''
  if (/^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10) // já é ISO
  const match = v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  if (!match) return v
  return `${match[3]}-${match[2]}-${match[1]}`
}

export function calculateAge(birthDate: Date): number {
  const today = new Date()
  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--
  }
  return age
}

export function calculateWorkYears(admissionDate: Date): number {
  const today = new Date()
  return today.getFullYear() - admissionDate.getFullYear()
}

export function daysUntil(date: Date): number {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const target = new Date(date)
  target.setHours(0, 0, 0, 0)
  const diff = target.getTime() - today.getTime()
  return Math.ceil(diff / (1000 * 60 * 60 * 24))
}

export function addWorkdays(date: Date, days: number): Date {
  const result = new Date(date)
  let added = 0
  while (added < days) {
    result.setDate(result.getDate() + 1)
    const dayOfWeek = result.getDay()
    if (dayOfWeek !== 0 && dayOfWeek !== 6) added++
  }
  return result
}
